import { useMemo } from 'react';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useGetCreditBalancesQuery, type CreditBalance, type CreditType } from '@/services/endpoints/credits';
import { isCreditsFeatureUnavailable } from '../utils/isCreditsFeatureUnavailable';

type UseCreditBalanceArgs = {
  type: CreditType;
  skip?: boolean;
};

export type UseCreditBalanceResult = {
  balance: CreditBalance | null;
  available: number;
  reserved: number;
  isKnown: boolean;
  isLoading: boolean;
  error: ReturnType<typeof useGetCreditBalancesQuery>['error'];
  isFeatureUnavailable: boolean;
};

export const useCreditBalance = ({ type, skip = false }: UseCreditBalanceArgs): UseCreditBalanceResult => {
  const workspace = useWorkspace();
  const { data, isLoading, isFetching, isError, error, isUninitialized } = useGetCreditBalancesQuery(
    { workspaceId: workspace.id, type },
    { skip: skip || !workspace.id }
  );

  const isFeatureUnavailable = isCreditsFeatureUnavailable(error);
  const isQueryLoading = isUninitialized || isLoading || isFetching;

  const balance = useMemo(() => {
    if (isFeatureUnavailable || isError || isQueryLoading || !data?.length) {
      return null;
    }

    return data.find((item) => item.creditType === type) ?? data[0] ?? null;
  }, [data, isError, isQueryLoading, isFeatureUnavailable, type]);

  return {
    balance,
    available: balance?.available ?? 0,
    reserved: balance?.reserved ?? 0,
    isKnown: balance != null,
    isFeatureUnavailable,
    isLoading: isQueryLoading,
    error,
  };
};
